import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import type {DigitalSignageProviderSubFlow} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';

/**
 * The order a signage panel publishes: the selected page's own order if that page has one, else the design-level order.
 *
 * Every provider panel needs this and none may publish without it, so the empty string is the single "no order yet" sentinel
 * they all guard on. Nullish coalescing is deliberate: a page order recorded as an empty string is a real recorded value and
 * stays empty rather than falling back to the design-level order.
 */
export const resolvePageOrderId = (productUsed: PosterProductDataForProcessDesign | undefined, pageHashedId: string): string => {
  return productUsed?.existingPageOrders?.[pageHashedId] ?? productUsed?.orderId ?? '';
};

interface DigitalSignageProviderDescriptor {
  labelKey: string;
  iconName: string;
  imgSrc?: string;
  gaType: string;
}

interface SignageProviderAvailability {
  provider: DigitalSignageProviderSubFlow;
  isPageSelectionPending: boolean;
  isNonFirstPageSelected: boolean;
}

/**
 * Displai and Fugo fetch the artwork themselves from the design id, which always resolves to the design's first page, so a
 * later page cannot be published to them. Every other provider publishes the page's own rendered order and is unaffected.
 */
const PROVIDERS_LIMITED_TO_FIRST_PAGE: readonly DigitalSignageProviderSubFlow[] = [SubFlowType.RAYDIANT_SUBDISPLAY, SubFlowType.FUGO_SUBDISPLAY];

export const canProviderPublishNonFirstPage = (provider: DigitalSignageProviderSubFlow): boolean => {
  return !PROVIDERS_LIMITED_TO_FIRST_PAGE.includes(provider);
};

/**
 * Test id for one provider's tile in the signage list, derived from the provider itself so adding a provider cannot ship
 * without a selectable id. `SMART_TV_SUBDISPLAY` becomes `PUBLISH-SIGNAGE-OPTION-SMART-TV`.
 */
export const getSignageProviderTestId = (provider: DigitalSignageProviderSubFlow): string => {
  const providerName = provider.replace(/_SUBDISPLAY$/, '').replace(/_/g, '-');

  return `${PosterEditorTestId.PUBLISH_SIGNAGE_OPTION}-${providerName}`;
};

/**
 * A provider tile is offered but not yet actionable: either we do not know which page the user means, or they picked a page
 * this provider cannot reach.
 */
export const isSignageProviderUnavailable = ({provider, isPageSelectionPending, isNonFirstPageSelected}: SignageProviderAvailability): boolean => {
  if (isPageSelectionPending) {
    return true;
  }

  return isNonFirstPageSelected && !canProviderPublishNonFirstPage(provider);
};

/** A design whose free edits are spent: it was paid for before, and publishing it again needs another purchase. */
export const shouldShowFreeEditsUsedUpBanner = (productUsed: PosterProductDataForProcessDesign | undefined): boolean => {
  const wasPreviouslyPurchased = productUsed?.purchaseId != null && Number(productUsed.purchaseId) > 0;

  return (productUsed?.needsPurchase ?? false) && wasPreviouslyPurchased;
};

/** The price shown on every provider tile — empty unless publishing this design actually needs a purchase. */
export const getSignageProviderPrice = (productUsed: PosterProductDataForProcessDesign | undefined): string => {
  if (productUsed?.needsPurchase !== true) {
    return '';
  }

  return productUsed.price ?? '';
};

/**
 * Presentation contract for every signage provider, shared by the signage list and the Recommended section's deep-link tiles so the two surfaces can never drift.
 */
export const DIGITAL_SIGNAGE_PROVIDER_DESCRIPTORS: Record<DigitalSignageProviderSubFlow, DigitalSignageProviderDescriptor> = {
  [SubFlowType.SMART_TV_SUBDISPLAY]: {
    labelKey: 'pmwjs_play_on_smart_tv',
    iconName: 'icon-tv',
    gaType: 'PlayOnSmartTV',
  },
  [SubFlowType.RAYDIANT_SUBDISPLAY]: {
    labelKey: 'pmwjs_raydiant',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-displai.png',
    gaType: 'Displai',
  },
  [SubFlowType.SCREEN_CLOUD_SUBDISPLAY]: {
    labelKey: 'pmwjs_screencloud',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-screencloud.png',
    gaType: 'ScreenCloud',
  },
  [SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY]: {
    labelKey: 'pmwjs_signagelive_widget',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-signagelive.png',
    gaType: 'SignageLive',
  },
  [SubFlowType.POSTERBOOKING_SUBDISPLAY]: {
    labelKey: 'pmwjs_posterbooking',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-poster-booking.png',
    gaType: 'PosterBooking',
  },
  [SubFlowType.FUGO_SUBDISPLAY]: {
    labelKey: 'pmwjs_fugo',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-fugo.png',
    gaType: 'Fugo',
  },
  [SubFlowType.YODECK_SUBDISPLAY]: {
    labelKey: 'pmwjs_yodeck',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-yodeck.png',
    gaType: 'Yodeck',
  },
  [SubFlowType.CLEAR_DIGITAL_SHO_SUBDISPLAY]: {
    labelKey: 'pmwjs_clear_digital_sho',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-clear-digital-sho.png',
    gaType: 'Clear Digital Sho',
  },
  [SubFlowType.VIZROM_SUBDISPLAY]: {
    labelKey: 'pmwjs_vizrom',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-vizrom.png',
    gaType: 'Vizrom',
  },
  [SubFlowType.DRIPBOARDS_SUBDISPLAY]: {
    labelKey: 'pmwjs_dripboards',
    iconName: 'icon-share-external',
    imgSrc: 'publishing-dripboards.png',
    gaType: 'Dripboards',
  },
};

const FUGO_PUBLISH_URL = 'https://www.fugo.ai/pmw';

const DISPLAI_DASHBOARD_URL = 'https://dash.displai.ai/applications';

/** Displai's PosterMyWall app, registered on their side; the deep link is scoped to it. */
const DISPLAI_POSTERMYWALL_APPLICATION_ID = '4cfa0ec9-dde0-4e24-b652-5ca5b7ae8651';

const DISPLAI_ATTRIBUTION_PARAMS = 'utm_source=dashboard&utm_medium=app&utm_campaign=postermywall';

/**
 * Fugo and Displai host their own copy of the design rather than embedding our viewer, so their panels hand the user off with
 * the design id in the URL and the partner pulls the artwork back through our public API.
 */
export const buildFugoDeepLink = (posterId: string): string => {
  return `${FUGO_PUBLISH_URL}?designId=${window.PMW.util.escapeHTML(posterId)}`;
};

/**
 * `posterId` is deliberately allowed to be undefined, and is interpolated unguarded, because that is what the panel
 * did before this was extracted: a design with no id yields the literal string "undefined" in the payload. Wrong, but
 * preserved — the behaviour is pinned by a test rather than changed here.
 */
export const buildDisplaiDeepLink = (posterId: string | undefined): string => {
  const applicationVariables = window.PMW.util.escapeHTML(`applicationVariables={"designId":"${posterId}"}`);

  return `${DISPLAI_DASHBOARD_URL}/${DISPLAI_POSTERMYWALL_APPLICATION_ID}?${applicationVariables}&${DISPLAI_ATTRIBUTION_PARAMS}`;
};

/**
 * Providers offered in the signage list, in display order — also the only ones eligible for a Recommended deep-link tile. Disabled providers (Vizrom, DripBoards) keep their descriptors above but are omitted here.
 */
export const ACTIVE_DIGITAL_SIGNAGE_PROVIDERS: readonly DigitalSignageProviderSubFlow[] = [
  SubFlowType.SMART_TV_SUBDISPLAY,
  SubFlowType.RAYDIANT_SUBDISPLAY,
  SubFlowType.SCREEN_CLOUD_SUBDISPLAY,
  SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY,
  SubFlowType.POSTERBOOKING_SUBDISPLAY,
  SubFlowType.FUGO_SUBDISPLAY,
  SubFlowType.YODECK_SUBDISPLAY,
  SubFlowType.CLEAR_DIGITAL_SHO_SUBDISPLAY,
];
